/**
 * The sync-run table, with its loading / error / empty states and its pager.
 *
 * PRESENTATION ONLY. It receives one page of rows and the SERVER's total; it
 * fetches nothing and counts nothing. `total` is deliberately a prop rather than
 * `rows.length`, which is one page and would hide every later one.
 *
 * The pager travels with the table because it is the table's own control: it
 * moves the window this component renders and nothing else on the page.
 *
 * SELECTION STAYS OUTSIDE. The selected run belongs to the page, so the table
 * only reports `onSelectRun(run)` and reads back a `selectedId` for the row
 * highlight. Holding it here would couple the table to a detail surface it does
 * not render.
 *
 * THE SELECTED ROW IS MARKED THREE WAYS, not one: `aria-current`, a left accent
 * bar and a tint. Colour alone would leave the current row invisible to a reader
 * who cannot separate it from the tint of a hover — and the tint is deliberately
 * NOT a status colour, because "this is the row you are reading" and "this run
 * failed" must not look like the same statement.
 */
import React from "react";

import { formatDateTime } from "../../../utils/datetime";
import BexioTablePagination from "../../../components/admin/bexio/workspace/BexioTablePagination";
import {
  BexioTableEmpty,
  BexioTableError,
  BexioTableSkeleton,
} from "../../../components/admin/bexio/workspace/BexioTableStates";
import BexioSyncScopeIcon from "./BexioSyncScopeIcon";
import SyncStatusPill from "./BexioSyncStatusPill";
import {
  fmtCount,
  formatDurationSeconds,
  runDurationSeconds,
  scopeCategoryBadge,
  scopeLabel,
  statusLabel,
} from "./bexioSyncRunsView";

export default function BexioSyncRunsTable({
  rows,
  total,
  offset,
  limit,
  loading,
  error,
  filtered,
  selectedId,
  onRetry,
  onResetFilters,
  onSelectRun,
  onOffsetChange,
  onLimitChange,
}) {
  return (
    <section
      aria-label="Sync-Läufe"
      className="overflow-hidden rounded-xl border border-border bg-card"
    >
      {loading && rows.length === 0 ? (
        <BexioTableSkeleton columns={7} />
      ) : error ? (
        <BexioTableError message={error} onRetry={onRetry} />
      ) : rows.length === 0 ? (
        <>
          <BexioTableEmpty
            filtered={filtered}
            noun="Sync-Läufe"
            onReset={onResetFilters}
          />
          <BexioTablePagination
            total={total}
            offset={offset}
            limit={limit}
            onOffsetChange={onOffsetChange}
            onLimitChange={onLimitChange}
            noun="Läufe"
            busy={loading}
          />
        </>
      ) : (
        <>
          {/* Horizontal scroll rather than a second mobile layout: seven numeric
              columns squeezed into 320px are unreadable, and the workspace
              already solves this the same way on the invoice table. */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th scope="col" className="w-[20%] px-4 py-2.5 text-xs font-medium text-muted-foreground">
                    Umfang
                  </th>
                  <th scope="col" className="w-[14%] whitespace-nowrap px-4 py-2.5 text-xs font-medium text-muted-foreground">
                    Status
                  </th>
                  <th scope="col" className="w-[20%] whitespace-nowrap px-4 py-2.5 text-xs font-medium text-muted-foreground">
                    Start
                  </th>
                  <th scope="col" className="w-[14%] whitespace-nowrap px-4 py-2.5 text-xs font-medium text-muted-foreground">
                    Dauer
                  </th>
                  <th scope="col" className="w-[10%] whitespace-nowrap px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">
                    Geholt
                  </th>
                  <th scope="col" className="w-[11%] whitespace-nowrap px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">
                    Gespeichert
                  </th>
                  <th scope="col" className="w-[11%] whitespace-nowrap px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">
                    Unverändert
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((run) => {
                  const category = scopeCategoryBadge(run.entity_scope);
                  const seconds = runDurationSeconds(run);
                  const isSelected = selectedId === run.id;
                  return (
                    <tr
                      key={run.id}
                      /* Global ARIA attribute, valid on a row: it names WHICH of
                         the listed runs the detail surface is showing. */
                      aria-current={isSelected ? "true" : undefined}
                      data-selected={isSelected ? "true" : undefined}
                      className={`border-b border-border last:border-0 ${
                        isSelected ? "bg-primary/5" : "hover:bg-muted/40"
                      }`}
                    >
                      {/* The accent bar is always there and only changes colour,
                          so selecting a row never shifts the column by 2px. */}
                      <td
                        className={`border-l-2 px-4 py-3 ${
                          isSelected ? "border-l-primary" : "border-l-transparent"
                        }`}
                      >
                        {/* A real button, so the row is reachable and operable
                            from the keyboard. Its name carries scope, status and
                            start — enough to tell two rows apart without sight. */}
                        <button
                          type="button"
                          onClick={() => onSelectRun(run)}
                          aria-label={`${scopeLabel(run.entity_scope)}, ${statusLabel(
                            run.status
                          )}, gestartet ${formatDateTime(
                            run.started_at
                          )}, Details anzeigen`}
                          className="flex max-w-full items-center gap-2 rounded text-left font-medium text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {/* Decoration only — `aria-hidden`, and the German
                              label beside it is what names the scope. */}
                          <BexioSyncScopeIcon scope={run.entity_scope} />
                          <span className="truncate hover:underline">
                            {scopeLabel(run.entity_scope)}
                          </span>
                          {/* Maintenance and technical runs are marked in words,
                              not by colour: their counters mean something else
                              than a data sync's. */}
                          {category ? (
                            <span className="shrink-0 whitespace-nowrap rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                              {category}
                            </span>
                          ) : null}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <SyncStatusPill status={run.status} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                        {formatDateTime(run.started_at)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 tabular-nums text-muted-foreground">
                        {run.finished_at ? formatDurationSeconds(seconds) : "Läuft"}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                        {fmtCount(run.fetched_count)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                        {fmtCount(run.upserted_count)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                        {fmtCount(run.unchanged_count)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* `total` is the server's count over the same filters — never
              `rows.length`, which is one page and would hide every later page. */}
          <BexioTablePagination
            total={total}
            offset={offset}
            limit={limit}
            onOffsetChange={onOffsetChange}
            onLimitChange={onLimitChange}
            noun="Läufe"
            busy={loading}
          />
        </>
      )}
    </section>
  );
}
